"use client";

import { colors } from "../../styles";

type RecordButtonState = "ready" | "countdown" | "recording" | "processing";

type Props = {
  state: RecordButtonState;
  onClick: () => void;
  progress: number;
  disabled?: boolean;
};

/**
 * Custom record button with state-based animations.
 * - Ready: Shows white outer ring (4px) with white inner circle (56x56)
 * - Countdown: Inner circle fades out
 * - Recording: Shows white outer ring with pink-500 rounded square (32x32), with pink-500 progress indicator showing elapsed time
 * - Progress: 0-100 value representing recording time elapsed
 */
export const RecordButton = ({
  state,
  onClick,
  disabled,
  progress = 0,
}: Props) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="relative flex size-8.5 items-center justify-center rounded-full border-4 border-white transition-opacity disabled:opacity-50"
    aria-label={
      state === "ready"
        ? "Start recording"
        : state === "countdown"
          ? "Recording starting"
          : state === "recording"
            ? "Stop recording"
            : "Processing recording"
    }
  >
    {/* Progress indicator overlay for recording state */}
    {state === "recording" && (
      <svg
        className="pointer-events-none absolute inset-0 -top-0.5 -left-0.5 h-8.5 w-8.5 -rotate-90"
        viewBox="0 0 76 76"
      >
        <circle
          cx="38"
          cy="38"
          r="35.5"
          fill="none"
          stroke={colors.pink500}
          strokeWidth="5"
          pathLength="100"
          strokeDasharray="100"
          strokeDashoffset={100 - progress}
          className="transition-all duration-300"
        />
      </svg>
    )}

    {/* Inner circle (ready state) */}
    {state === "ready" && (
      <div className="size-7 rounded-full bg-white transition-all duration-300" />
    )}

    {/* Inner circle fading out (countdown state) */}
    {state === "countdown" && (
      <div className="animate-out fade-out zoom-out size-7 rounded-full bg-white transition-all duration-300 [animation-fill-mode:forwards]" />
    )}

    {/* Rounded square (recording state) */}
    {state === "recording" && (
      <div className="animate-in fade-in zoom-in size-4 rounded bg-pink-500 transition-all duration-300" />
    )}

    {/* Spinner (processing state) */}
    {state === "processing" && (
      <div className="size-7 animate-spin rounded-full border-2 border-white border-t-transparent" />
    )}
  </button>
);
